import type {
  IMapBannerVisualizationFilterValues,
  TCreateBannerParams,
  TDeleteBannerParams,
  TToggleBannerParams,
  TUpdateBannerParams,
} from 'core/model/interfaces/banners';
import { https, httpsMultipart } from 'shared/utils/https';
import type {
  IGetBannerBySegmentGroupListParams,
  IGetBannerBySegmentGroupListResponseDTO,
  IGetBannerListParams,
  IGetBannerListResponseDTO,
  IGetSegmentedBannerCarouselListResponseDTO,
  IPublishSegmentedBannersRequestDTO,
  TBannerDTO,
} from './banners.dto';

const versions = {
  v1: '/v1',
} as const;
const baseURL = `${versions.v1}/banners` as const;

export const bannersData = {
  /**
   * It returns a list of banners based on the provided filters.
   *
   * - If the mode is 'segment', it will return banners that are segmented by distributors and price lists.
   * - If the mode is 'customer_list', it will return banners that are segmented by customer codes.
   */
  getBannerCarouselList: async (
    params: IMapBannerVisualizationFilterValues,
  ): Promise<IGetSegmentedBannerCarouselListResponseDTO> => {
    const url = `${versions.v1}/segments/banners`;

    const response = await https.get<IGetSegmentedBannerCarouselListResponseDTO>(url, {
      params,
    });

    const { data } = response;

    return data;
  },
  /**
   * Retrieves the complete list of banners.
   *
   * This function fetches all banners with some filters applied.
   */
  getBannerListByTarget: async (params: IGetBannerListParams): Promise<IGetBannerListResponseDTO> => {
    const url = baseURL;

    const response = await https.get<IGetBannerListResponseDTO>(url, { params });

    const { data } = response;

    return data;
  },
  getBannerBySegmentGroupList: async (
    params: IGetBannerBySegmentGroupListParams,
  ): Promise<IGetBannerBySegmentGroupListResponseDTO> => {
    const url = `${baseURL}/group-list`;

    const response = await https.get<IGetBannerBySegmentGroupListResponseDTO>(url, {
      params,
    });

    const { data } = response;

    return data;
  },
  createBanner: async (
    params: TCreateBannerParams,
    formData: FormData,
  ): Promise<Pick<Pick<TBannerDTO, 'id'>, 'id'>> => {
    const url = baseURL;

    const response = await httpsMultipart.post<Pick<TBannerDTO, 'id'>>(url, formData);

    const { data } = response;

    return data;
  },
  updateBanner: async (params: TUpdateBannerParams, formData: FormData): Promise<void> => {
    const url = `${baseURL}/${params.id}`;

    const response = await httpsMultipart.patch<void>(url, formData);

    const { data } = response;

    return data;
  },
  toggleBanner: async (params: TToggleBannerParams, formData: FormData): Promise<void> => {
    const { id } = params;

    const url = `${baseURL}/${id}`;

    const response = await httpsMultipart.patch<void>(url, formData);

    const { data } = response;

    return data;
  },
  deleteBanner: async (params: TDeleteBannerParams): Promise<void> => {
    const url = `${baseURL}/${params.id}`;

    await https.delete<void>(url);
  },
  publishSegmentedBannerList: async (params: IPublishSegmentedBannersRequestDTO): Promise<void> => {
    const url = `${versions.v1}/segments/banners`;

    const { data } = await https.patch<void>(url, params);

    return data;
  },
};
